const items = [
  {
    title: '1. ABOUT PRIVACY NOTICE',
    summary: (
      <>
        <p>
          When you use Joblio, you trust us with your personal data. We’re
          committed to keeping that trust and protecting your personal data.
          That starts with helping you understand our privacy practices.
        </p>

        <p>
          This notice describes the personal data we collect, how it’s used and
          shared, and your choices regarding this data and rights. We recommend
          that you read this document carefully.
        </p>
        <p>
          Along with our Terms of Service and Cookie Policy, this policy
          explains how we (Joblio Inc.) collect and handle your information
          across all of our website, mobile apps and other services. We'll
          review this policy from time to time to make sure it's up-to-date. If
          we make changes, we'll post the latest version here. When we make
          significant changes, we'll let you know when you next access our
          services, or by other communications.
        </p>
        <p>
          Protecting your personal data is our priority. This Privacy Notice
          explains how Joblio processes and protects personal data about:
          candidates who register with us for roles that we are managing for
          other employers and for roles at Joblio, or through our website, app,
          social media sites or other sources;
          <ul>
            <li>prospective candidates,</li>
            <li>business contacts at our clients; and</li>
            <li>users of our websites;</li>
          </ul>
          (collectively "you" or “your").
          <p>
            If you would like to contact us, please see the “How to contact us”
            section of this policy.
          </p>
        </p>
      </>
    ),
    details: (
      <>
        <p>
          You'll come across the term 'personal data' a lot in this policy. By
          that, we mean any information relating to you as an identifiable
          individual. For example, this includes information that may of itself
          identify you such as your name, contact details, internet protocol
          ("IP") address, cookie strings or device IDs, as well as information
          that may not on its own identify you, but which we store alongside
          such identifiers, such as how you are using our services or the
          country you are in when you do so.
        </p>
        <p>
          We can only speak for ourselves so this policy doesn't cover the
          collection or use of your information by any of the employing
          companies (let's call them "Employers") we feature. That means when
          you apply for an exact job, your information will be processed by them
          in accordance with their own Privacy Notice and terms and conditions.
          But don't worry, we'll always find partners who also protect personal
          data.
        </p>

        <strong>How will we notify you of changes to this policy?</strong>
        <p>
          We'll notify you before we make changes to this policy and give you
          the opportunity to review the revised policy before you choose to
          continue using our Products.
        </p>
      </>
    )
  },
  {
    title: '2. What personal data we collect and why do we use it?',
    summary: (
      <>
        <p>
          Everyone who uses our services will have bits and pieces of
          information about them collected, stored and processed. However, we
          don't collect more information than we need.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          We can't help you find your dream job without information. So, when
          you use our services, we have to collect, keep and share some personal
          data and ask that you agree to that in line with this policy. We
          collect information in three ways:
        </p>
        <p>You give it to us voluntarily</p>
        <p>
          Information you choose to give us might include personal data needed
          for you to find job. This includes name, surnames, contact details,
          etc. It can also include content like the reviews or to share with
          other clients, chatting with others. Most importantly, it's always up
          to you whether or not you choose to give us this kind of information.
        </p>
        <p>We generate or collect it automatically</p>
        <p>
          We generate or collect some information from your computer or device
          automatically as you use our services. This includes stuff like your
          IP address, information about the device and browser you are using to
          access our services, the website URL you visited us from. It also
          includes details of the job applications you have made via Joblio. We
          may also know your location from your mobile or your IP address. This
          helps us to improve your experience and make sure you receive the
          content that's most relevant to you.
        </p>
        <p>
          Our services are not intended for children under 18 years of age, and
          no one under the age of 18 should provide any information to, on or
          via our services. We don't knowingly collect personal data from
          children under 18, and will delete any that we learn we have collected
          or received.
        </p>
      </>
    )
  },
  {
    title: '2.1 Candidates',
    summary: (
      <>
        <p>
          When you register with Joblio to seek employment opportunities through
          our platform or services, we collect and process personal data
          required to provide those services. This may include:
        </p>

        <ul>
          <li>
            Identification and contact information (e.g. name, phone number,
            email address, address)
          </li>
          <li>
            Employment history, qualifications, and skills (e.g. information in
            your CV/resume)
          </li>
          <li>Details about your job preferences and salary expectations</li>
          <li>Information about current benefits and availability for work</li>
        </ul>

        <p>
          Where required or permitted by applicable law, we may also process
          special category or sensitive data, including:
        </p>

        <ul>
          <li>
            Health-related information (e.g. to support reasonable workplace
            accommodations)
          </li>
          <li>
            Diversity data (e.g. race, ethnicity, sexual orientation, religion)
            for equal opportunity monitoring
          </li>
          <li>
            Information on unspent criminal convictions, where required by law
            or a potential employer
          </li>
        </ul>

        <p>
          If you engage with us online (e.g. via our website, emails, mobile
          apps), we may collect additional data such as:
        </p>

        <p>Your use of the Joblio website or app</p>
        <ul>
          <li>IP address, device data, and browsing behavior</li>
          <li>
            Interaction with emails or job alerts (e.g. clicks or forwards)
          </li>
        </ul>

        <p>
          We may also receive your personal data from third parties, including:
        </p>
        <ul>
          <li>Referees or references you provide</li>
          <li>Previous employers (to confirm work history)</li>
          <li>Educational institutions (to verify qualifications)</li>
          <li>
            Background check providers, such as the Disclosure and Barring
            Service or credit reference agencies
          </li>
          <li>
            Public platforms (e.g. LinkedIn or other professional or social
            media) to supplement your profile
          </li>
          <li>
            Clients or recruiters with whom you’ve interacted during a
            recruitment process
          </li>
        </ul>
      </>
    )
  },
  {
    title: '2.2 Business clients',
    summary: (
      <>
        <p>
          If you are a representative of a current or prospective client, we
          collect and process:
        </p>

        <ul>
          <li>Your name, job title, and professional contact details</li>
          <li>Details of your communications with us</li>
          <li>Feedback you provide about our services or candidates</li>
          <li>
            We may also collect your information from publicly available
            sources, such as professional networking sites, or event attendance
            lists where sharing of delegate data is allowed.
          </li>
        </ul>
      </>
    )
  },
  {
    title: '2.3 Users of the Joblio website or apps',
    summary: (
      <>
        <p>When you use our digital services, we collect:</p>

        <ul>
          <li>
            Technical data such as IP address, browser type, and device
            information
          </li>
          <li>
            Information about how you interact with our websites or mobile apps
            (e.g. pages viewed, time spent)
          </li>
          <li>
            Information submitted through contact forms or other inquiries
          </li>
        </ul>

        <p>
          We use cookies and similar tracking technologies to collect and
          analyze this data. For more information, please see our Cookie Policy.
        </p>

        <p>
          We only use your information where you've given us your consent, where
          its necessary to deliver the services you've requested, where it's
          necessary to exercise or comply with legal rights or obligations, or
          for normal business purposes of the kind set out in this policy.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          <strong>
            <i>How we use personal data to perform contracts.</i>
          </strong>
        </p>
        <ul>
          <li>
            Ensuring our services are accessible and usable by you on your
            specific device, for example by altering the way we display
            information to you based on your device type;
          </li>
          <li>
            Generating results in response to your search queries, for example
            providing you with a list of the vacancies, job options that best
            meet your requirements;
          </li>
          <li>
            Contacting you with messages, notifications or alerts that are
            necessary in order to deliver our services requested, such as job
            confirmation emails or other essential communications like
            notifications about changes or updates to our services or to this
            policy, our Cookie Policy or our Terms of Service;
          </li>
          <li>
            Responding to communications you have sent us, such as via our
            customer service help centre or on social media;
          </li>
          <li>
            Displaying reviews, images and any other content and media you
            submit to Joblio or upload to the Joblio Services (we'll call this
            "User Content"), and providing you with the tools to manage and
            delete this content; and
          </li>
          <li>
            Where you have created an account with us, maintaining and
            administering this and ensuring that it is kept up-to-date and
            accessible;
          </li>
        </ul>
        <p>
          To improve or optimize our services and to otherwise protect or
          further our legitimate interests. This includes pretty serious reasons
          like fraud prevention and security but also fun ones like product
          enhancement so we can keep making Joblio even better for our
          customers.
        </p>

        <strong>
          <i>Our legitimate interests.</i>
        </strong>

        <ul>
          <li>
            Security and fraud-prevention purposes, for example by storing your
            IP address to record where a request has originated from, or to
            protect our services against misuse of our APIs or from automated
            software agents.
          </li>
          <li>
            Providing customer service or help services to you in relation to
            any questions you raise or issues you might encounter while using
            our services.
          </li>
          <li>
            Understanding and evaluating how, when and why people are using
            Joblio for the purposes of delivering, evaluating, improving and
            optimising the delivery of our services. For example:
            <br />
            a) monitoring the numbers of users who use our services and visit
            our website;
            <br />
            b) analysing at an aggregate level what different groups of users
            may be viewing or clicking on within our services in order to better
            understand which features or functionality are more popular or
            helpful than others;
            <br />
            c)reviewing and moderating any User Content that you upload to the
            Joblio Services; running A/B tests that display alternate versions
            of our services to different groups of users, in order to evaluate
            the positive or negative impact of such changes on our users'
            experience of our services. For example, we may change the
            prominence of a button, its colour or its placement for different
            users visiting our site to help us understand how to make our
            service easier to use or to more effectively draw users' attention
            to the most relevant and important information; and
            <br />
            d) measuring the effectiveness of our communications through
            recording and analysing whether emails we have sent you have been
            opened.
            <br />
          </li>
          <li>
            Personalising your experience of our services in order to ensure
            your experience with Joblio is as helpful, efficient and relevant as
            possible. For example:
            <br />
            a) we will use cookies to remember your language and currency
            preferences and may also personalise your experience by highlighting
            or recommending certain information or job options that we believe
            are relevant to you based on your search history on Joblio and use
            of our services;
            <br />
            b) if you have asked us to send you newsletters or other marketing
            communications, we may tailor the timings or content of these to
            reflect what we believe will be of most interest to you, such as by
            using the fact that you've recently searched for a job in Poland to
            prompt us to ask whether you'd like to search for a job in Gdansk as
            well;
            <br />
            c) we may use your location information to provide you information
            about nearest Joblio office; and
            <br />
            d) displaying relevant advertisements to you about our services or
            other services that we think may be of interest to you.
            <br />
            e) Personalising your experience involves us creating a profile and
            timeline of your activity on our services. However, we are careful
            to ensure that our activities do not involve undertaking any
            profiling or automated decision making that might have any legal or
            other significant effects on you.
            <br />
            f) We may use your data to train our machine learning algorithms to
            help us personalise the Services. We will not use directly
            identifying information such as your contact details for these
            purposes.
          </li>
          <li>
            Informing strategic business decisions through creating aggregated
            datasets of anonymised data that allow us to generate helpful
            insights into patterns or trends within the labour industry.
          </li>
          <li>
            Contacting you in order to invite you to participate in genuine
            market research.
          </li>
        </ul>

        <p>
          Where we process your personal data based on a legitimate interest we
          will only do so where the processing is relevant, adequate and limited
          to what is necessary for the purpose it was collected for. And of
          course, we'll always ensure our legitimate interests don't unfairly
          impact on your own rights and freedoms.
        </p>

        <p>
          <strong>
            <i>When we have your consent.</i>
          </strong>
        </p>

        <p>We will obtain your consent for purposes such as:</p>
        <ul>
          <li>
            sending you emails for services you request such as new vacancies
            Alerts;
          </li>
          <li>
            sending you direct marketing communications via email, push
            notification or other means where you have consented to us doing so;
          </li>
          <li>
            storing certain cookies on your device, where doing so requires
            consent; and
          </li>
          <li>
            recording and evaluating feedback you give us where you agree to
            take part in one-to-one user testing.
          </li>
        </ul>
        <p>
          Where we process your personal data based only on your consent, you
          can withdraw this consent at any time either by using the
          functionality provided within the appropriate product feature or by
          contacting us.
        </p>
        <p>
          <strong>
            Where it is necessary to exercise or comply with our own legal
            rights or obligations.
          </strong>
        </p>
        <p>
          We may need to retain and use your information in connection with
          legal claims, or for compliance, regulatory and auditing purposes. For
          example we may retain information where we are required by law, or if
          we are compelled to do so by a court order or regulatory body. Also,
          when you exercise any applicable legal rights you have to access,
          amend or delete your personal data, we may request identification and
          verification documents from you for the purpose of confirming your
          identity.
        </p>
      </>
    )
  },
  {
    title: '3. How long do we store your personal data?',
    summary: (
      <>
        <p>
          We only keep your data for as long as we need it, or are required to
          for legal reasons. We'll then either delete it or anonymise it so it
          doesn't identify you. We treat data differently depending on what it's
          used for, but you can ask us to delete your personal data at any time.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          We keep your personal data only for as long as we need to. This
          depends on why it was collected, or if we have a continuing legal
          basis to do so (such as to fulfil a contract between us, perform a
          service you requested or for our legitimate interests). Rest assured,
          if we no longer have a reason or legal requirement to process your
          personal data, we will delete it or store it in a way so that it no
          longer identifies you.
        </p>
        <p>
          We have different retention policies for different types of personal
          data, taking into account:
        </p>
        <ul>
          <li>The purpose for collecting the personal data;</li>
          <li>How long it will take to fulfil that purpose; and</li>
          <li>
            Any specific reason or overriding legal obligation to retain the
            personal data for a specific amount of time.
          </li>
        </ul>
        <p>
          Also, if you have an account with us, we will keep personal data such
          as your email address, name and other details so you can log in and
          access our services for as long as you have an account. We may keep
          other information indefinitely - such as site activity linked to an IP
          address - which we use to help us understand our clients, improve our
          products and services and protect our business interests.
        </p>
        <p>
          We may also delete User Content, which may include personal data, in
          accordance with our terms of service and community guidelines.
        </p>
        <p>
          No matter how long the retention period, you can ask us to delete your
          personal data in certain circumstances. Find out more about how you
          can manage your personal data in the What are your choices and rights
          section of this policy.
        </p>
      </>
    )
  },
  {
    title:
      '4. When is your information shared with or collected by third parties?',
    summary: (
      <>
        <p>
          We share your information only where you ask us to, where it's a
          necessary part of doing business with you and providing you with the
          services, or where we need to for legal reasons. Companies you apply
          to via Joblio will collect your data in line with their own policies.
          Sometimes companies that advertise via our services may also collect
          your information (see Do we use cookies or other similar
          technologies). Finally, companies that help us deliver our services
          will also collect and use your data on our behalf.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          If you are a candidate, we share your personal data with clients who
          have vacancies for jobs which you are interested in.
        </p>
        <p>
          We also share your personal data and, where necessary, special
          category data with third party service providers who perform services
          and functions on our behalf, such as:
        </p>
        <ul>
          <li>conducting employment reference checks;</li>
          <li>conducting qualification checks;</li>
          <li>carrying out criminal convictions checks (as required);</li>
          <li>verifying details you have provided from third party sources;</li>
          <li>conducting psychometric evaluations or skills tests;</li>
          <li>hosting personal data for us;</li>
          <li>providing professional advice to us;</li>
          <li>providing data analytics to us;</li>
          <li>
            carrying out testing and development work on our business technology
            systems;
          </li>
          <li>administering surveys or competitions on our behalf;</li>
          <li>assisting us to communicate with you; and</li>
          <li>
            providing research and mailing house or other direct marketing
            services.
          </li>
        </ul>
        <p>
          We contractually require minimum standards of confidentiality and data
          protection from our third party service providers. If we need to send
          personal data outside the EEA, we will ensure that adequate safeguards
          are in place, such as the Model Clauses.
        </p>
        <p>
          <strong>
            <i>
              Sharing personal data with third parties who process it only under
              our instruction
            </i>
          </strong>
        </p>
        <p>
          We share information relating to our users with selected third parties
          who provide us with a variety of different services that support the
          delivery of our services (let's call them "Third Party Processors").
          These Third Party Processors range from providers of employers,
          technical infrastructure to customer service and authentication tools.
          We require any Third Party Processor which handles information on our
          behalf to do so pursuant to contractual terms which require that the
          information is kept secure, is processed in accordance with applicable
          data protection laws, and used only as we have instructed and not for
          that Third Party Processor's own purposes (unless you have explicitly
          consented to them doing so).
        </p>
        <p>
          Third Party Processors may be located in, or process your information,
          outside of the country in which you are based. Where our use of a
          Third Party Processor involves the transfer of personal data from
          within Europe to a location outside of the European Economic Area, we
          will put in place appropriate measures to ensure that the personal
          data is adequately protected in that location, most often by applying
          European Commission-approved standard contractual clauses alongside
          robust security checks.
        </p>
        <p>
          The types of Third Party Processors we may share elements of your
          personal data with include:
        </p>
        <ul>
          <li>Employers, who receive your job applications;</li>
          <li>
            providers of email management and distribution tools - for example,
            if you sign up to receive Joblio newsletters or other marketing
            messages we will manage the delivery of these to you using a third
            party email distribution tool;
          </li>
          <li>
            providers of security and fraud prevention services - for example,
            we use these providers to identify automated software agents that
            might disrupt our services or to prevent misuse of our APIs;
          </li>
          <li>
            providers of data aggregation and analytics software services that
            enable us to effectively monitor and optimise the delivery of our
            services;
          </li>
          <li>providers of tracking tools that we use to monitor instances;</li>
          <li>
            providers of software platforms that assist us in communicating or
            providing customer support services to you - for example, we manage
            and respond to any messages you send to us via our help centre using
            a third party communications management tool;
          </li>
          <li>
            providers of online cloud storage services and other essential IT
            support services;
          </li>
        </ul>
        <p>
          <strong>
            <i>
              Sharing your information with third parties, or allowing them to
              collect it, for processing outside of our control
            </i>
          </strong>
        </p>
        <p>
          If you are applying for a job with an Employer from within our
          platform, any personal data you submit as part of the process will be
          shared with the relevant Employer and/or a third party contracted to
          provide services on their behalf, in order to enable the application
          to be processed.
        </p>
        <p>
          We may also share personal data with the Employer for their, or our,
          standard verification and fraud-detection purposes.
        </p>
        <p>
          In order to prevent and detect fraud, we may share information about
          you with the fraud prevention services providers we cooperate with
          ("Fraud Prevention Providers").
        </p>
        <p>
          Employers and Fraud Prevention Providers may be located in, or process
          your information, outside of the country in which you are based. The
          regulations in these countries may not provide the same level of
          protection of personal data as the regulations in your own country of
          residence.
        </p>
        <p>
          We will share personal data with third parties where you expressly
          authorise us to.
        </p>
        <p>
          Publicly accessible personal data included in User Content such as
          your name, likeness and the content of reviews and posts may be shared
          and/or made available to carefully selected third parties with whom we
          have a contractual relationship for the purpose of displaying this
          content for their similar services.
        </p>
        <p>
          We may share and/or make available your publicly accessible personal
          data such as your name, profile picture, reviews to carefully selected
          third parties with whom we have a contractual relationship.
        </p>
        <p>
          Find out more about how you can manage your personal data when it is
          shared in this way in the What are your choices and rights section of
          this policy.
        </p>
        <p>
          Certain information may also be collected from you by third parties
          such as advertisers, marketing networks and affiliates (see the How do
          we advertise section below) using cookies and similar technologies.
          For example your IP address and events relating to your activity like
          searches you have carried out or pages you have viewed on Joblio may
          be collected and transmitted to these third parties to allow them to
          serve relevant advertising to you across the web. The information that
          is collected in this way will never include your name, contact details
          or other information that would enable you to be identified in the
          offline world. You can find out more about advertising in Joblio
          including how you can opt out of certain types of advertising in the
          How do we advertise section of this policy and in our Cookie Policy.
        </p>

        <p>
          <strong>Disclosing information for legal and other reasons</strong>
        </p>
        <p>
          We may disclose your information where necessary to enforce our Terms
          of Service or other agreements, or to a prospective or ultimate buyer
          if Joblio itself (or part of our business) is sold. We may also
          disclose your information if necessary to prevent, detect or prosecute
          illegal or suspected illegal activities, including fraud, or to
          prevent other damage or where necessary in response to legally binding
          requests, legal action against us, or to enforce our rights and
          claims.
        </p>
      </>
    )
  },
  {
    title: '5. How do we keep your personal data secure?',
    summary: (
      <>
        <p>
          Your data is securely stored in data centres around the world - the
          exact location depends on where you are when you use Joblior. Your
          data will only be processed by suppliers who provide appropriate
          contractual safeguards for the information they process. Sometimes
          your data may be stored in countries with different levels of security
          to your own but we always make sure their standards meet ours.
        </p>
        <p>
          We have put in place security measures to prevent your personal data
          from being accidentally lost, used, altered, disclosed, or accessed
          without authorisation. We also allow access to your personal data only
          to those employees and partners who have a business need to know such
          data. They will only process your personal data on our instructions
          and they must keep it confidential.
        </p>
        <p>
          We have procedures in place to deal with any suspected personal data
          breach and will notify you and any applicable regulator of a breach if
          we are legally required to.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          We store the information we collect from you on secure servers in
          various locations, depending on where you are in the world when you
          access our site. Currently, we use servers that are located in the
          USA. These servers are provided and/or supported by third parties
          acting under our instruction (see When is your information shared with
          or collected by third parties? for more information).
        </p>
        <p>
          Sometimes the countries that your data is transferred to, or stored
          in, may have different, or less stringent, data protection and
          security standards than your own. However, subject to the local laws
          in these countries, we'll provide the safeguards needed to protect
          your data regardless of location. We do this through a combination of
          appropriate technical, organisational and administrative security
          measures, and by putting in place the necessary legal contracts to
          back up these requirements. For example, we will only store European
          users' personal data outside the European Economic Area where a
          European Commission-approved method of validating the transfer has
          been put in place.
        </p>
        <p>
          If you have a question about the steps we take to safeguard your
          personal data, please contact us using the details set out in this
          Privacy Policy (see Who are we and how can you contact us?).
        </p>
      </>
    )
  },
  {
    title: '6. Do we use cookies or similar technologies?',
    summary: (
      <>
        <p>
          We use cookies and similar technologies to help deliver, optimise,
          personalise and analyse our services and for advertising purposes.
        </p>
      </>
    ),
    details: (
      <>
        <p>
          We use a combination of cookies and other technologies such as
          pixels/web beacons and tracking codes (explained below) to collect
          information for use in line with the purposes set out in this policy.
        </p>
        <p>
          <strong>What is a Cookie?</strong>
          <p>
            A cookie is a small data file that is stored on your computer or
            device. Cookies record things like your preferences and settings,
            helping to give us the information we need to provide you with the
            most consistent and relevant experience. We also use cookies to
            record information about experiments or new improvements we might
            test to track how you use our services and find out whether they
            were effective.
          </p>
          <p>
            Cookies on our website may be set by us, third parties we're working
            with, or independent third parties (such as advertisers).
          </p>
        </p>

        <p>
          <strong>What are Web Beacons and Pixels?</strong>
          <p>
            Web beacons or pixels are small, transparent image files inside a
            web page or email. We use them to understand how you interact with
            our services, or if you've redirected to a Travel Supplier's website
            or app. We might use the pixel to confirm if you went on to book
            with them, and what you booked. We also use pixels to gather
            information such as whether you've opened an email, so that we can
            improve our communications to you.
          </p>
        </p>

        <p>
          <strong>What are tracking codes?</strong>
          <p>
            Tracking codes are snippets of code placed in the page to measure
            things like visits and interactions. We use tracking codes to find
            out more about how you interact with our services, the adverts you
            see and more generally, how you use us.
          </p>
          <p>
            Please see our Cookie Policy for more information and details of the
            types of cookies and similar technologies we use.
          </p>
        </p>
      </>
    )
  },
  {
    title: '7. What are your choices and rights?',
    summary: (
      <>
        <p>
          If you've got a Joblio account, you can access, edit, download or
          delete the key personal data associated with your profile at any time
          by going to your Profile. From here you can also manage your
          subscription and marketing preferences.
        </p>
        <p>
          You also have rights in relation to the personal data we have about
          you, which we explain in more detail below. You can exercise these
          rights by getting in touch with us via our Help Centre.
        </p>
        <p>
          If you are a California resident, learn more about your rights under
          the{' '}
          <a
            href="https://oag.ca.gov/privacy/ccpa"
            target="_blank"
            rel="noopener noreferrer"
          >
            California Consumer Privacy Act
          </a>
          .
        </p>
      </>
    ),
    details: (
      <>
        <p>
          You have the right to ask us for a copy of your personal data; to
          correct, delete or restrict processing of your personal data; and to
          obtain the personal data you have provided to us in a structured,
          machine-readable format. In addition, you can object to the processing
          of your personal data in some circumstances (in particular, where we
          don't have to process the data to meet a contractual or other legal
          requirement).
        </p>
        <p>
          Where we have asked for your consent, you may withdraw consent at any
          time. If you ask to withdraw your consent to Joblio processing your
          data, this will not affect any processing which has already taken
          place at that time.
        </p>
        <p>
          These rights may be limited, for example if fulfilling your request
          would reveal personal data about another person, or if you ask us to
          delete information which we are required by law or have compelling
          legitimate interests to keep. If you have unresolved concerns, you
          have the right to complain to a data protection authority.
        </p>
      </>
    )
  },
  {
    title: '8. COMPLAINTS',
    summary: (
      <>
        <p>
          To the extent that you're unable to satisfactorily exercise any of
          these rights via your Profile, you can do so by contacting our Data
          Protection Officer by post at Joblio,{' '}
          <a
            href="https://www.google.com.ua/maps/place/1+Seneca+St,+Buffalo,+NY+14210,+%D0%A1%D0%A8%D0%90/@42.8799178,-78.8768611,19z/data=!4m6!3m5!1s0x89d31236c11ba747:0xcdc579a05a3922ff!8m2!3d42.8799168!4d-78.8756018!16s%2Fg%2F1q67tpkdz?hl=ru&entry=ttu"
            target="_blank"
            rel="noopener noreferrer"
          >
            1 Seneca St., Suite 29-M49, Buffalo, NY., 14203
          </a>
          , or electronically via{' '}
          <a href="mailto: dpo@joblio.co">dpo@joblio.co</a>.
        </p>
        <p>
          We are committed to protecting your personal data but if for some
          reason you are not happy with any aspect of how we collect and use
          your data, you have the right to complain to the data protection
          authority of the country in which you are based. We would be grateful
          if you would contact us first if you do have a complaint so that we
          can try to resolve it for you.
        </p>
      </>
    ),
    details: null
  },
  {
    summary: (
      <>
        <p>
          <strong>Who are we and how can you contact us?</strong>
        </p>
        <p>
          Joblio Inc. is a company registered in the USA.
          <br />
          Joblio Inc. is the Data Controller of any information collected from
          you by us.
        </p>
        <p>
          Our registered office address is:{' '}
          <a
            href="https://www.google.com.ua/maps/place/1+Seneca+St,+Buffalo,+NY+14210,+%D0%A1%D0%A8%D0%90/@42.8799178,-78.8768611,19z/data=!4m6!3m5!1s0x89d31236c11ba747:0xcdc579a05a3922ff!8m2!3d42.8799168!4d-78.8756018!16s%2Fg%2F1q67tpkdz?hl=ru&entry=ttu"
            target="_blank"
            rel="noopener noreferrer"
          >
            1 Seneca St., Suite 29-M49, Buffalo, NY., 14203
          </a>
          .
        </p>
      </>
    ),
    details: null
  }
];

export default items;
